import React, { useState, useEffect, useContext, useCallback } from 'react';
import Layout from '@/components/Layout';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { formatCurrencyInput, parseCurrency } from '@/utils/currencyFormat';
import PremiumGate from '@/components/PremiumGate';
import UpgradePrompt from '@/components/UpgradePrompt';
import {
  Target,
  Plus,
  TrendingUp,
  Calendar,
  CheckCircle,
  Trash2,
  Edit,
  Bell,
  BellOff
} from 'lucide-react';

const Goals = () => {
  const { user, token } = useContext(AuthContext);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    target_amount: '',
    target_date: '',
    category: 'general',
    description: '',
    notification_enabled: true,
    notification_frequency: 'weekly'
  });

  const fetchGoals = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/goals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGoals(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Esta funcionalidad requiere suscripción Premium', { id: 'premium-error' });
      } else if (error.response?.status === 401) {
        toast.error('Sesión expirada. Por favor, inicia sesión nuevamente.', { id: 'auth-error' });
      } else {
        toast.error(error.response?.data?.detail || 'Error al cargar objetivos');
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    const isPremiumOrPro = (user?.subscription_tier === 'premium' || user?.subscription_tier === 'pro' || user?.role === 'admin' || user?.is_manual_grant);
    if (user && token && isPremiumOrPro) {
      fetchGoals();
    } else {
      setLoading(false);
    }
  }, [user, token, fetchGoals]);



  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Convertir target_amount a número
      const goalData = {
        ...formData,
        target_amount: parseFloat(formData.target_amount)
      };

      if (editingGoal) {
        await axios.put(
          `${API}/goals/${editingGoal.id}`,
          goalData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Objetivo actualizado exitosamente');
      } else {
        await axios.post(
          `${API}/goals`,
          goalData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Objetivo creado exitosamente');
      }

      setFormData({
        name: '',
        target_amount: '',
        target_date: '',
        category: 'general',
        description: '',
        notification_enabled: true,
        notification_frequency: 'weekly'
      });
      setShowCreateForm(false);
      setEditingGoal(null);
      fetchGoals();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Esta funcionalidad requiere suscripción Premium', { id: 'premium-error' });
      } else if (error.response?.status === 401) {
        toast.error('Sesión expirada. Por favor, inicia sesión nuevamente.', { id: 'auth-error' });
      } else {
        toast.error(error.response?.data?.detail || 'Error al guardar objetivo');
      }
    }
  };

  const handleDelete = async (goalId) => {
    if (!window.confirm('¿Estás seguro de eliminar este objetivo?')) return;

    try {
      await axios.delete(`${API}/goals/${goalId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Objetivo eliminado');
      fetchGoals();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Esta funcionalidad requiere suscripción Premium', { id: 'premium-error' });
      } else if (error.response?.status === 401) {
        toast.error('Sesión expirada. Por favor, inicia sesión nuevamente.', { id: 'auth-error' });
      } else {
        toast.error(error.response?.data?.detail || 'Error al eliminar objetivo');
      }
    }
  };

  const handleAddProgress = async (goalId) => {
    const amount = prompt('¿Cuánto deseas agregar al objetivo?');
    if (!amount || isNaN(amount)) return;

    try {
      await axios.post(
        `${API}/goals/${goalId}/progress`,
        { goal_id: goalId, amount: parseFloat(amount) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Progreso agregado');
      fetchGoals();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Esta funcionalidad requiere suscripción Premium', { id: 'premium-error' });
      } else if (error.response?.status === 401) {
        toast.error('Sesión expirada. Por favor, inicia sesión nuevamente.', { id: 'auth-error' });
      } else {
        toast.error(error.response?.data?.detail || 'Error al agregar progreso');
      }
    }
  };

  const handleEdit = (goal) => {
    setEditingGoal(goal);
    setFormData({
      name: goal.name,
      target_amount: goal.target_amount,
      target_date: goal.target_date,
      category: goal.category,
      description: goal.description || '',
      notification_enabled: goal.notification_enabled,
      notification_frequency: goal.notification_frequency
    });
    setShowCreateForm(true);
  };

  const calculateProgress = (current, target) => {
    return Math.min((current / target) * 100, 100);
  };

  const getDaysRemaining = (targetDate) => {
    const target = new Date(targetDate);
    const today = new Date();
    const diffTime = target - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getCategoryIcon = (category) => {
    const icons = {
      vacation: '✈️',
      emergency: '🚨',
      investment: '📈',
      education: '📚',
      general: '🎯'
    };
    return icons[category] || '🎯';
  };

  return (
    <Layout>
      <PremiumGate fallback={
        <div className="p-4 md:p-8 max-w-3xl mx-auto">
          <UpgradePrompt
            title="Objetivos Financieros"
            message="Establece metas de ahorro para vacaciones, emergencias o proyectos y sigue tu progreso automáticamente."
            feature="Metas ilimitadas y recordatorios personalizados"
          />
        </div>
      }>
        <div className="p-4 md:p-8 max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-100 rounded-xl">
                <Target className="w-8 h-8 text-purple-600" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-slate-900">
                  Objetivos Financieros
                </h1>
                <p className="text-slate-600">
                  Establece y alcanza tus metas de ahorro
                </p>
              </div>
            </div>
            <Button
              onClick={() => {
                setEditingGoal(null);
                setFormData({
                  name: '',
                  target_amount: '',
                  target_date: '',
                  category: 'general',
                  description: '',
                  notification_enabled: true,
                  notification_frequency: 'weekly'
                });
                setShowCreateForm(!showCreateForm);
              }}
              className="w-full md:w-auto bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
            >
              <Plus className="w-5 h-5 mr-2" />
              Nuevo Objetivo
            </Button>
          </div>
        </div>

        {/* Create/Edit Form */}
        {showCreateForm && (
          <Card className="p-6 mb-6 border-2 border-purple-200">
            <h3 className="text-xl font-semibold mb-4">
              {editingGoal ? 'Editar Objetivo' : 'Crear Nuevo Objetivo'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Nombre del Objetivo</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ej: Vacaciones 2026"
                    required
                  />
                </div>
                <div>
                  <Label>Categoría</Label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full p-2 border border-slate-300 rounded-lg"
                  >
                    <option value="general">General</option>
                    <option value="vacation">Vacaciones</option>
                    <option value="emergency">Fondo de Emergencia</option>
                    <option value="investment">Inversión</option>
                    <option value="education">Educación</option>
                  </select>
                </div>
                <div>
                  <Label>Cantidad Objetivo (MXN)</Label>
                  <Input
                    type="text"
                    value={formData.target_amount ? formatCurrencyInput(formData.target_amount) : ''}
                    onChange={(e) => {
                      const rawValue = parseCurrency(e.target.value);
                      setFormData({ ...formData, target_amount: rawValue });
                    }}
                    placeholder="$10,000.00"
                    required
                  />
                </div>
                <div>
                  <Label>Fecha Objetivo</Label>
                  <Input
                    type="date"
                    value={formData.target_date}
                    onChange={(e) => setFormData({ ...formData, target_date: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div>
                <Label>Descripción (Opcional)</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Detalles sobre este objetivo..."
                  rows={3}
                />
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.notification_enabled}
                    onChange={(e) => setFormData({ ...formData, notification_enabled: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-slate-700">Habilitar notificaciones</span>
                </label>
                {formData.notification_enabled && (
                  <select
                    value={formData.notification_frequency}
                    onChange={(e) => setFormData({ ...formData, notification_frequency: e.target.value })}
                    className="p-2 border border-slate-300 rounded-lg text-sm"
                  >
                    <option value="daily">Diario</option>
                    <option value="weekly">Semanal</option>
                    <option value="monthly">Mensual</option>
                  </select>
                )}
              </div>
              <div className="flex gap-3">
                <Button type="submit" className="bg-purple-600 hover:bg-purple-700">
                  {editingGoal ? 'Actualizar' : 'Crear'} Objetivo
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowCreateForm(false);
                    setEditingGoal(null);
                  }}
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </Card>
        )}

        {/* Goals List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
            <p className="mt-4 text-slate-600">Cargando objetivos...</p>
          </div>
        ) : goals.length === 0 ? (
          <Card className="p-12 text-center">
            <Target className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">
              No tienes objetivos aún
            </h3>
            <p className="text-slate-600 mb-6">
              Comienza creando tu primer objetivo financiero
            </p>
            <Button
              onClick={() => setShowCreateForm(true)}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Plus className="w-5 h-5 mr-2" />
              Crear Primer Objetivo
            </Button>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {goals.map((goal) => {
              const progress = calculateProgress(goal.current_amount, goal.target_amount);
              const daysRemaining = getDaysRemaining(goal.target_date);
              const isCompleted = goal.status === 'completed';

              return (
                <Card
                  key={goal.id}
                  className={`p-6 hover:shadow-lg transition-all ${isCompleted ? 'border-2 border-green-500 bg-green-50' : ''
                    }`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">{getCategoryIcon(goal.category)}</span>
                      <div>
                        <h3 className="font-semibold text-slate-900">{goal.name}</h3>
                        <p className="text-sm text-slate-600 capitalize">{goal.category}</p>
                      </div>
                    </div>
                    {isCompleted && (
                      <CheckCircle className="w-6 h-6 text-green-500" />
                    )}
                  </div>

                  {goal.description && (
                    <p className="text-sm text-slate-600 mb-4">{goal.description}</p>
                  )}

                  {/* Progress Bar */}
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-slate-600">Progreso</span>
                      <span className="font-semibold text-purple-600">{progress.toFixed(1)}%</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-3">
                      <div
                        className={`h-3 rounded-full transition-all ${isCompleted ? 'bg-green-500' : 'bg-gradient-to-r from-purple-600 to-pink-600'
                          }`}
                        style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Amount Info */}
                  <div className="flex items-center justify-between mb-4 p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="text-xs text-slate-600">Actual</p>
                      <p className="font-bold text-slate-900">
                        ${goal.current_amount.toLocaleString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-600">Objetivo</p>
                      <p className="font-bold text-slate-900">
                        ${goal.target_amount.toLocaleString()}
                      </p>
                    </div>
                  </div>

                  {/* Days Remaining */}
                  <div className="flex items-center gap-2 text-sm text-slate-600 mb-4">
                    <Calendar className="w-4 h-4" />
                    <span>
                      {daysRemaining > 0
                        ? `${daysRemaining} días restantes`
                        : daysRemaining === 0
                          ? 'Vence hoy'
                          : `Vencido hace ${Math.abs(daysRemaining)} días`}
                    </span>
                  </div>

                  {/* Notification Status */}
                  <div className="flex items-center gap-2 text-sm text-slate-600 mb-4">
                    {goal.notification_enabled ? (
                      <>
                        <Bell className="w-4 h-4 text-blue-600" />
                        <span>Notificaciones: {goal.notification_frequency}</span>
                      </>
                    ) : (
                      <>
                        <BellOff className="w-4 h-4 text-slate-400" />
                        <span>Sin notificaciones</span>
                      </>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    {!isCompleted && (
                      <Button
                        onClick={() => handleAddProgress(goal.id)}
                        className="flex-1 bg-purple-600 hover:bg-purple-700"
                        size="sm"
                      >
                        <TrendingUp className="w-4 h-4 mr-1" />
                        Agregar
                      </Button>
                    )}
                    <Button
                      onClick={() => handleEdit(goal)}
                      variant="outline"
                      size="sm"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => handleDelete(goal.id)}
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
        </div>
      </PremiumGate>
    </Layout>
  );
};

export default Goals;
